import { Navigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Card, Col, Row, Tag, Typography } from 'antd';
import { motion, useReducedMotion, type TargetAndTransition, type Variants } from 'framer-motion';
import {
  ShoppingCartOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  FileTextOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import type { RootState } from '@/redux';

const { Title, Paragraph, Text } = Typography;

const PRIMARY = '#a4193d';

const createFadeUp = (shouldReduceMotion: boolean): Variants => ({
  hidden: {
    opacity: 0,
    y: shouldReduceMotion ? 0 : 18,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: shouldReduceMotion ? 0 : 0.45,
      ease: 'easeOut',
    },
  },
});

const createStaggerContainer = (shouldReduceMotion: boolean): Variants => ({
  hidden: {},
  visible: {
    transition: {
      staggerChildren: shouldReduceMotion ? 0 : 0.08,
      delayChildren: shouldReduceMotion ? 0 : 0.08,
    },
  },
});

const FEATURES = [
  {
    icon: <ShoppingCartOutlined style={{ fontSize: 36, color: PRIMARY }} />,
    title: 'POS Bán hàng',
    desc: 'Giao diện bán hàng nhanh, hỗ trợ nhiều phương thức thanh toán, in hoá đơn nhiệt tức thì.',
  },
  {
    icon: <DatabaseOutlined style={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Quản lý tồn kho',
    desc: 'Theo dõi tồn kho real-time, nhập/xuất kho, kiểm kê định kỳ và cảnh báo hàng sắp hết.',
  },
  {
    icon: <BarChartOutlined style={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Báo cáo kinh doanh',
    desc: 'Doanh thu, lợi nhuận, top sản phẩm — tổng hợp theo ngày/tuần/tháng với biểu đồ trực quan.',
  },
  {
    icon: <FileTextOutlined style={{ fontSize: 36, color: PRIMARY }} />,
    title: 'Hoá đơn điện tử',
    desc: 'Phát hành HĐĐT theo nghị định 123/2020/NĐ-CP, ký số, gửi khách qua email tự động.',
  },
];

const PLANS = [
  {
    name: 'Lite',
    price: 'Miễn phí',
    sub: 'mãi mãi',
    tag: null,
    features: ['1 cửa hàng', '1 nhân viên', 'POS cơ bản', 'Quản lý sản phẩm', 'Báo cáo ngày'],
    cta: 'Bắt đầu',
    primary: false,
  },
  {
    name: 'Starter',
    price: '299.000đ',
    sub: '/ tháng',
    tag: null,
    features: ['1 cửa hàng', '5 nhân viên', 'POS đầy đủ', 'Tồn kho', 'Khách hàng CRM', 'Báo cáo nâng cao'],
    cta: 'Dùng thử 14 ngày',
    primary: false,
  },
  {
    name: 'Professional',
    price: '699.000đ',
    sub: '/ tháng',
    tag: 'Phổ biến nhất',
    features: [
      '3 cửa hàng',
      '15 nhân viên',
      'Tất cả tính năng Starter',
      'Hoá đơn điện tử',
      'Cầm đồ & Trả góp',
      'MISA AMIS sync',
    ],
    cta: 'Dùng thử 14 ngày',
    primary: true,
  },
  {
    name: 'Enterprise',
    price: 'Liên hệ',
    sub: '',
    tag: null,
    features: [
      'Không giới hạn cửa hàng',
      'Không giới hạn nhân viên',
      'Tất cả tính năng',
      'SLA 99.9%',
      'Hỗ trợ onsite',
      'Tuỳ chỉnh theo yêu cầu',
    ],
    cta: 'Liên hệ tư vấn',
    primary: false,
  },
];

export function LandingPage() {
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const shouldReduceMotion = Boolean(useReducedMotion());
  const fadeUp = createFadeUp(shouldReduceMotion);
  const staggerContainer = createStaggerContainer(shouldReduceMotion);
  const cardHover: TargetAndTransition | undefined = shouldReduceMotion
    ? undefined
    : {
      y: -6,
      scale: 1.01,
      transition: { duration: 0.2, ease: 'easeOut' },
    };

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <div style={{ fontFamily: 'Inter, -apple-system, sans-serif', color: '#1a1a2e' }}>
      {/* ── Header ── */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          padding: '0 5vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: PRIMARY,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            G
          </div>
          <span style={{ fontWeight: 700, fontSize: 18, color: PRIMARY }}>ERP Gold</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/auth/login" style={{ color: '#555', fontWeight: 500 }}>
            Đăng nhập
          </Link>
          <Button
            type="primary"
            style={{ background: PRIMARY, borderColor: PRIMARY, fontWeight: 600 }}
            href="/auth/register"
          >
            Đăng ký miễn phí
          </Button>
        </div>
      </header>

      {/* ── Hero ── */}
      <section
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #2d1b3d 60%, #a4193d 100%)',
          padding: '96px 5vw 80px',
          textAlign: 'center',
          color: '#fff',
        }}
      >
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={fadeUp}>
            <Tag
              color="rgba(255,255,255,0.15)"
              style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', marginBottom: 24, fontSize: 13 }}
            >
              🇻🇳 Giải pháp ERP dành riêng cho tiệm vàng Việt Nam
            </Tag>
          </motion.div>
          <motion.div variants={fadeUp}>
            <Title
              style={{ color: '#fff', fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 800, margin: '0 auto 20px', maxWidth: 720 }}
            >
              Quản lý tiệm vàng thông minh — đơn giản, nhanh, đáng tin
            </Title>
          </motion.div>
          <motion.div variants={fadeUp}>
            <Paragraph
              style={{ color: 'rgba(255,255,255,0.75)', fontSize: 18, maxWidth: 560, margin: '0 auto 40px' }}
            >
              Từ POS bán hàng, tồn kho đến hoá đơn điện tử — ERP Gold giúp chủ tiệm vàng kiểm soát
              toàn bộ hoạt động kinh doanh trên một nền tảng duy nhất.
            </Paragraph>
          </motion.div>
          <motion.div variants={fadeUp} style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              size="large"
              type="primary"
              href="/auth/register"
              style={{
                color: PRIMARY,
                borderColor: '#fff',
                fontWeight: 700,
                height: 48,
                paddingInline: 32,
                fontSize: 16,
              }}
            >
              Dùng thử miễn phí
            </Button>
            <Button
              size="large"
              ghost
              style={{ height: 48, paddingInline: 32, fontSize: 16, borderColor: 'rgba(255,255,255,0.5)' }}
              href="#pricing"
            >
              Xem bảng giá
            </Button>
          </motion.div>
          <motion.div variants={fadeUp}>
            <Text style={{ display: 'block', color: 'rgba(255,255,255,0.5)', marginTop: 20, fontSize: 13 }}>
              Miễn phí 14 ngày · Không cần thẻ tín dụng · Huỷ bất cứ lúc nào
            </Text>
          </motion.div>
        </motion.div>
      </section>

      {/* ── Features ── */}
      <section style={{ padding: '80px 5vw', background: '#fafafa' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
          >
            <Title level={2} style={{ textAlign: 'center', marginBottom: 8 }}>
              Mọi thứ bạn cần, trong một nền tảng
            </Title>
            <Paragraph style={{ textAlign: 'center', color: '#666', fontSize: 16, marginBottom: 48 }}>
              Thiết kế riêng cho đặc thù kinh doanh vàng bạc đá quý tại Việt Nam.
            </Paragraph>
          </motion.div>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <Row gutter={[24, 24]}>
              {FEATURES.map((f) => (
                <Col xs={24} sm={12} lg={6} key={f.title}>
                  <motion.div variants={fadeUp} whileHover={cardHover} style={{ height: '100%' }}>
                    <Card
                      bordered={false}
                      style={{ height: '100%', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
                      bodyStyle={{ padding: 28 }}
                    >
                      <div style={{ marginBottom: 16 }}>{f.icon}</div>
                      <Title level={4} style={{ marginBottom: 8 }}>{f.title}</Title>
                      <Paragraph style={{ color: '#666', margin: 0 }}>{f.desc}</Paragraph>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" style={{ padding: '80px 5vw', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
          >
            <Title level={2} style={{ textAlign: 'center', marginBottom: 8 }}>
              Gói dịch vụ phù hợp với quy mô của bạn
            </Title>
            <Paragraph style={{ textAlign: 'center', color: '#666', fontSize: 16, marginBottom: 48 }}>
              Bắt đầu miễn phí, nâng cấp khi cần. Không phí ẩn.
            </Paragraph>
          </motion.div>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            <Row gutter={[20, 24]}>
              {PLANS.map((plan) => (
                <Col xs={24} sm={12} xl={6} key={plan.name}>
                  <motion.div variants={fadeUp} whileHover={cardHover} style={{ height: '100%' }}>
                    <Card
                      bordered={plan.primary}
                      style={{
                        height: '100%',
                        borderRadius: 12,
                        borderColor: plan.primary ? PRIMARY : '#f0f0f0',
                        borderWidth: plan.primary ? 2 : 1,
                        boxShadow: plan.primary
                          ? `0 8px 32px rgba(164,25,61,0.15)`
                          : '0 2px 12px rgba(0,0,0,0.05)',
                        position: 'relative',
                        overflow: 'visible',
                      }}
                      bodyStyle={{ padding: 28 }}
                    >
                      {plan.tag && (
                        <Tag
                          color={PRIMARY}
                          style={{
                            position: 'absolute',
                            top: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontWeight: 600,
                            fontSize: 12,
                            padding: '2px 12px',
                          }}
                        >
                          {plan.tag}
                        </Tag>
                      )}
                      <Title level={4} style={{ marginBottom: 4, color: plan.primary ? PRIMARY : undefined }}>
                        {plan.name}
                      </Title>
                      <div style={{ marginBottom: 20 }}>
                        <span style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e' }}>{plan.price}</span>
                        {plan.sub && (
                          <span style={{ color: '#999', fontSize: 14, marginLeft: 4 }}>{plan.sub}</span>
                        )}
                      </div>
                      <div style={{ marginBottom: 24 }}>
                        {plan.features.map((feat) => (
                          <div key={feat} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <CheckOutlined style={{ color: PRIMARY, fontSize: 13, flexShrink: 0 }} />
                            <span style={{ fontSize: 14, color: '#444' }}>{feat}</span>
                          </div>
                        ))}
                      </div>
                      <Button
                        type={plan.primary ? 'primary' : 'default'}
                        block
                        size="large"
                        href={plan.name === 'Enterprise' ? 'mailto:sales@erpgold.vn' : '/auth/register'}
                        style={
                          plan.primary
                            ? { background: PRIMARY, borderColor: PRIMARY, fontWeight: 600 }
                            : { fontWeight: 600 }
                        }
                      >
                        {plan.cta}
                      </Button>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer
        style={{
          background: '#1a1a2e',
          color: 'rgba(255,255,255,0.6)',
          padding: '40px 5vw',
          textAlign: 'center',
          fontSize: 14,
        }}
      >
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 700, color: '#fff', fontSize: 16 }}>ERP Gold</span>
            <span style={{ margin: '0 12px' }}>·</span>
            <Link to="/auth/login" style={{ color: 'rgba(255,255,255,0.6)' }}>Đăng nhập</Link>
            <span style={{ margin: '0 12px' }}>·</span>
            <Link to="/auth/register" style={{ color: 'rgba(255,255,255,0.6)' }}>Đăng ký</Link>
            <span style={{ margin: '0 12px' }}>·</span>
            <a href="mailto:support@erpgold.vn" style={{ color: 'rgba(255,255,255,0.6)' }}>Liên hệ</a>
          </div>
          <div>© {new Date().getFullYear()} ERP Gold. Bảo lưu mọi quyền.</div>
        </motion.div>
      </footer>
    </div>
  );
}
